import React from "react";
import { FxRate } from "../../../services/fx";
import { formatDateTime } from "../../../utils/dateFormat";
import { useUserCountry } from "../../../hooks/useUserCountry";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import styles from "./FxTable.module.scss";

interface FxTableProps {
  rows: FxRate[];
  page: number;
  pages: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort: (field: string) => void;
  onPage: (page: number) => void;
}

export default function FxTable({
  rows,
  page,
  pages,
  sortBy,
  sortOrder,
  onSort,
  onPage,
}: FxTableProps) {
  const country = useUserCountry();
  // [2026-02-13] Maya: Responsive - show cards on mobile/tablet
  const { isMobile, isTablet } = useMediaQuery();
  const showCards = isMobile || isTablet;

  const formatDate = (dateString: string) => {
    try {
      return formatDateTime(dateString, country);
    } catch {
      return "N/A";
    }
  };

  const formatRate = (rate: number) => {
    return rate.toFixed(4);
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return "↕️";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  const handleSort = (field: string) => {
    onSort(field);
  };

  const renderPagination = () => {
    if (pages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <nav aria-label="FX rates pagination" className={styles.paginationNav}>
        <ul className={styles.paginationList}>
          <li className={styles.paginationItem}>
            <button
              type="button"
              className="btn-figma-1 btn-sm"
              onClick={() => page > 1 && onPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </button>
          </li>

          {startPage > 1 && (
            <>
              <li key="first-page" className={styles.paginationItem}>
                <button type="button" className="btn-figma-1 btn-sm" onClick={() => onPage(1)}>
                  1
                </button>
              </li>
              {startPage > 2 && (
                <li key="start-ellipsis" className={styles.paginationItem}>
                  <span className={`u-P2-16-medium ${styles.paginationEllipsis}`}>...</span>
                </li>
              )}
            </>
          )}

          {pageNumbers.map((pageNum) => {
            const isActive = page === pageNum;
            return (
              <li key={pageNum} className={styles.paginationItem}>
                <button
                  type="button"
                  className={isActive ? "btn-black btn-sm" : "btn-figma-1 btn-sm"}
                  onClick={() => onPage(pageNum)}
                  aria-current={isActive ? "page" : undefined}
                >
                  {pageNum}
                </button>
              </li>
            );
          })}

          {endPage < pages && (
            <>
              {endPage < pages - 1 && (
                <li key="end-ellipsis" className={styles.paginationItem}>
                  <span className={`u-P2-16-medium ${styles.paginationEllipsis}`}>...</span>
                </li>
              )}
              <li key="last-page" className={styles.paginationItem}>
                <button
                  type="button"
                  className="btn-figma-1 btn-sm"
                  onClick={() => onPage(pages)}
                >
                  {pages}
                </button>
              </li>
            </>
          )}

          <li className={styles.paginationItem}>
            <button
              type="button"
              className="btn-figma-1 btn-sm"
              onClick={() => page < pages && onPage(page + 1)}
              disabled={page === pages}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  if (rows.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <h5 className="text-muted">No exchange rates found</h5>
          <p className="text-muted">Try adjusting your filters or refresh the data.</p>
        </div>
      </div>
    );
  }

  // [2026-02-13] Maya: Mobile/tablet card view for FX rates
  if (showCards) {
    return (
      <div>
        {rows.map((row) => (
          <div key={`${row.base}-${row.quote}`} className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex gap-2">
                  <span className="badge fx-badge-base">{row.base}</span>
                  <span className="text-muted">→</span>
                  <span className="badge fx-badge-quote">{row.quote}</span>
                </div>
                <strong className="u-H4-18-bold">{formatRate(row.rate)}</strong>
              </div>
              <div className="d-flex justify-content-between text-muted small">
                <span>Source: {row.source || "N/A"}</span>
                <span>{formatDate(row.updatedAt)}</span>
              </div>
            </div>
          </div>
        ))}
        {renderPagination()}
      </div>
    );
  }

  return (
    <div className="card" style={{ width: "100%", maxWidth: "100%" }}>
      <div className="card-body p-0">
        <div className="table-responsive" style={{ width: "100%", overflowX: "auto" }}>
          <table className="table table-hover mb-0" style={{ width: "100%", minWidth: "700px" }}>
            <thead className="table-light">
              <tr>
                <th
                  className="cursor-pointer user-select-none"
                  onClick={() => handleSort("base")}
                >
                  Base {getSortIcon("base")}
                </th>
                <th
                  className="cursor-pointer user-select-none"
                  onClick={() => handleSort("quote")}
                >
                  Quote {getSortIcon("quote")}
                </th>
                <th
                  className="cursor-pointer text-end user-select-none"
                  onClick={() => handleSort("rate")}
                >
                  Rate {getSortIcon("rate")}
                </th>
                <th
                  className="cursor-pointer user-select-none"
                  onClick={() => handleSort("updatedAt")}
                >
                  Updated {getSortIcon("updatedAt")}
                </th>
                <th
                  className="cursor-pointer user-select-none"
                  onClick={() => handleSort("source")}
                >
                  Source {getSortIcon("source")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.base}-${row.quote}`}>
                  <td>
                    <span className="badge fx-badge-base">{row.base}</span>
                  </td>
                  <td>
                    <span className="badge fx-badge-quote">{row.quote}</span>
                  </td>
                  <td className="text-end">
                    <strong>{formatRate(row.rate)}</strong>
                  </td>
                  <td>
                    <small className="text-muted">{formatDate(row.updatedAt)}</small>
                  </td>
                  <td>
                    <span className="badge fx-badge-source">{row.source || "N/A"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {renderPagination()}
      </div>
    </div>
  );
}
